import React, { useEffect, useRef, useState } from "react";

/**
 * Re-authentication prompt for a single sensitive change. The password is held
 * only long enough to make the request - it is never stored or logged.
 */
export default function ConfirmPassword({ open, title, detail, confirmLabel = "Confirm", onConfirm, onCancel }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setError("");
      setBusy(false);
      // let the dialog paint before stealing focus
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err.message || "Could not confirm");
      setBusy(false);
      setPassword("");
      inputRef.current?.focus();
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          {detail && <div className="modal-detail">{detail}</div>}
        </div>

        <form onSubmit={submit} className="modal-body" autoComplete="off">
          <label className="field-label" htmlFor="confirm-pw">Confirm with your password</label>
          <input
            id="confirm-pw"
            ref={inputRef}
            className="input"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          {error && <div className="modal-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={!password || busy}>
              {busy ? "Confirming…" : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
